import { differenceInCalendarDays } from 'date-fns'
import { BedDouble, Lock, LogIn, LogOut, Moon } from 'lucide-react'
import type { CleaningTeam, PropertyBlocks, PropertyCalendarStay } from '@/api/client'
import {
  BLOCK_BAR_CLASSES,
  HOLD_BAR_CLASSES,
  blockLabel,
  type CalendarBlockLike,
} from '@/components/calendar/block-bars'
import {
  CLEANING_STATE_META,
  cleaningLabel,
  cleaningState,
  cleaningTitle,
  isCleaningDrawn,
} from '@/components/calendar/cleaning-bars'
import { EventBlockIcon } from '@/components/calendar/event-blocks'
import type { EventBlockSpec } from '@/components/calendar/event-block-meta'
import {
  depositEventBlocks,
  reviewEventBlocks,
  supplyEventBlocks,
} from '@/components/calendar/event-block-specs'
import { GAP_BAR_CLASSES } from '@/components/calendar/gap-bars'
import { CALENDAR_COLOR_CLASSES, getCalendarColorKey } from '@/components/calendar/month-grid'
import { localDate } from '@/components/cleaning/presentation'
import { parseCalendarDate } from '@/lib/calendar-date'
import { getChannelLabel } from '@/lib/format-utils'
import { describeGapSpan, gapOpenValue } from '@/lib/gap-events'
import { isReservationCancelled } from '@/lib/reservation-utils'
import { supplyOpenValue } from '@/lib/supply-events'
import { shortDay } from './agenda'
import { overlapPhrase, statusOf } from './block-utils'
import { eventRows, gapCoversDay, nonEmptyGroups, type DayListGroup } from './day-list'
import type { CalendarLayerId } from './layers'
import {
  cleaningOpenValue,
  openBlockSheet,
  stayOpenValue,
  type CalendarSheetNav,
} from './sheet-open'
import { DEPOSIT_SECTION, REVIEW_SECTION } from './stay-sheet'
import type { CalendarData, CalendarRange } from './use-calendar-data'

export interface DayGroupsInput {
  data: CalendarData
  range: CalendarRange
  isVisible: (id: CalendarLayerId) => boolean
  /** Whether the read's blocks layer came back ok. */
  blocksRead: boolean
  blocksData: PropertyBlocks | undefined
  teams: CleaningTeam[]
  crewLoading: boolean
  go: CalendarSheetNav['go']
}

/** A block's state in the words its own sheet uses, left out when it is plainly "Blocked". */
function blockStatusWords(
  block: CalendarBlockLike,
  blocksData: PropertyBlocks | undefined
): string | null {
  if (block.kind !== 'owner') return null
  const record = blocksData?.owner_blocks.find(candidate => candidate.block_id === block.id)
  if (record) {
    const label = statusOf(record).label
    return label === 'Blocked' ? null : label
  }
  // Before the blocks read lands the month read cannot say "uncertain" from
  // "failed", so it does not guess between them.
  switch (block.sync_status) {
    case 'pending':
      return 'Syncing'
    case 'deleting':
      return 'Removing'
    case 'failed':
      return 'Sync problem'
    default:
      return null
  }
}

/**
 * Everything the grid is drawing on `day`, as the day list's rows: only the
 * layers that are switched on and were read, so the list never says more than
 * the grid does.
 *
 * `agenda` is the phone's list of the whole month, where a thing that spans
 * days must not be repeated on every one of them: a stay is listed on its
 * check-in day and its check-out day (and once on the month's first day when
 * it began earlier, so a 33-night stay is three rows, not 33), and a gap or
 * a block only on its first day in the month. The day sheet lists a day as it
 * stands, so it keeps its stays to the day's arrivals and departures and lists
 * every block covering the day.
 */
export function buildDayGroups(
  { data, range, isVisible, blocksRead, blocksData, teams, crewLoading, go }: DayGroupsInput,
  day: string,
  agenda = false
): DayListGroup[] {
  const {
    stays,
    cleaningJobs,
    timeZone,
    markersByDay,
    supplyMarkersByDay,
    reviewMarkersByDay,
    gapItems,
    blockItems,
    blockPool,
  } = data
  const monthStart = range.start
  const openDeposit = (reservationId: string) =>
    go(stayOpenValue(reservationId), { section: DEPOSIT_SECTION })
  const openReview = (reservationId: string) =>
    go(stayOpenValue(reservationId), { section: REVIEW_SECTION })
  const openSupplyItem = (marker: { category: string; day: string }) =>
    go(supplyOpenValue(marker.category, marker.day))

  type StayKind = 'in' | 'out' | 'staying'
  const stayKindOn = (stay: PropertyCalendarStay): StayKind | null => {
    if (stay.check_in === day) return 'in'
    if (stay.check_out === day) return 'out'
    return agenda &&
      day === monthStart &&
      stay.check_in &&
      stay.check_out &&
      stay.check_in < day &&
      stay.check_out > day
      ? 'staying'
      : null
  }
  const STAY_ORDER: Record<StayKind, number> = { out: 0, staying: 1, in: 2 }
  const liveStays = isVisible('stays')
    ? stays
        .flatMap(stay => {
          const kind = isReservationCancelled(stay) ? null : stayKindOn(stay)
          return kind ? [{ stay, kind }] : []
        })
        // The guest leaving is listed before the one arriving.
        .sort((a, b) => STAY_ORDER[a.kind] - STAY_ORDER[b.kind])
    : []
  const stayRows = liveStays.map(({ stay, kind }) => {
    const Icon = kind === 'in' ? LogIn : kind === 'out' ? LogOut : BedDouble
    const word = kind === 'in' ? 'Check-in' : kind === 'out' ? 'Check-out' : 'Staying'
    const nights = stay.nights ? `${stay.nights} ${stay.nights === 1 ? 'night' : 'nights'}` : null
    const guest = stay.guest_name || 'Guest'
    const detail = [
      getChannelLabel(stay.channel),
      nights,
      // Said once, where the stay is listed, in place of a row on every day between.
      agenda && kind !== 'out' && stay.check_out ? `leaves ${shortDay(stay.check_out)}` : null,
    ]
      .filter(Boolean)
      .join(' · ')
    return {
      key: `stay:${stay.reservation_id}:${kind}`,
      tileClassName: CALENDAR_COLOR_CLASSES[getCalendarColorKey(stay)],
      icon: <Icon className="h-4 w-4" aria-hidden="true" />,
      title: `${word}: ${guest}`,
      detail,
      ariaLabel: `${word}: ${guest}, ${detail} - open stay`,
      testId: 'day-list-stay-row',
      data: { 'data-reservation-id': stay.reservation_id },
      onOpen: () => go(stayOpenValue(stay.reservation_id)),
    }
  })

  const cleaningRows = isVisible('cleaning')
    ? cleaningJobs
        .filter(
          job =>
            isCleaningDrawn(job) &&
            (job.local_date ?? localDate(job.scheduled_at, timeZone)) === day
        )
        .map(job => {
          const team = teams.find(candidate => candidate.id === job.team_id)
          const state = cleaningState(job)
          const { Icon, classes } = CLEANING_STATE_META[state]
          const label = cleaningLabel(job, team, crewLoading)
          // The state's sentence only where the label does not already say it.
          const detail =
            state === 'needs_decision' || state === 'not_required'
              ? cleaningTitle(job, team, crewLoading)
              : null
          return {
            key: `cleaning:${job.id}`,
            tileClassName: classes,
            icon: <Icon className="h-4 w-4" aria-hidden="true" />,
            title: label,
            detail,
            ariaLabel: `${label} - open cleaning`,
            testId: 'day-list-cleaning-row',
            data: { 'data-job-id': job.id, 'data-cleaning-state': state },
            onOpen: () => go(cleaningOpenValue(job.id)),
          }
        })
    : []

  const blockIcon = (spec: EventBlockSpec) => (
    <EventBlockIcon meta={spec.meta} className="h-4 w-4" />
  )
  const depositRows = isVisible('deposits')
    ? eventRows(depositEventBlocks(markersByDay.get(day) ?? [], timeZone, openDeposit), blockIcon)
    : []
  const supplyRows = isVisible('supplies')
    ? eventRows(supplyEventBlocks(supplyMarkersByDay.get(day) ?? [], openSupplyItem), blockIcon)
    : []

  const reviewRows = isVisible('reviews')
    ? eventRows(reviewEventBlocks(reviewMarkersByDay.get(day) ?? [], openReview), blockIcon)
    : []

  const gapRows = isVisible('orphan_gaps')
    ? gapItems
        .filter(gap =>
          agenda
            ? (gap.first_night < monthStart ? monthStart : gap.first_night) === day
            : gapCoversDay(gap, day)
        )
        .map(gap => ({
          key: `gap:${gap.first_night}`,
          tileClassName: GAP_BAR_CLASSES,
          icon: <Moon className="h-4 w-4" aria-hidden="true" />,
          title: 'Empty nights',
          detail: describeGapSpan(gap),
          ariaLabel: `Empty nights: ${describeGapSpan(gap)} - open orphan gap`,
          testId: 'day-list-gap-row',
          data: { 'data-gap-first-night': gap.first_night },
          onOpen: () => go(gapOpenValue(gap.first_night)),
        }))
    : []

  // Every block and Uplisting-app hold covering the day, so a block that sits
  // under or inside another is still reachable. The grid draws them on lanes;
  // here they are rows. The phone's agenda lists each block once, on its first
  // day in the month (like a gap), with a phrase saying what it overlaps.
  const blockRows =
    isVisible('blocks') && blocksRead
      ? blockItems
          .filter(block =>
            agenda
              ? block.start < range.end &&
                block.end_exclusive > monthStart &&
                (block.start < monthStart ? monthStart : block.start) === day
              : block.start <= day && day < block.end_exclusive
          )
          .map(block => {
            const first = parseCalendarDate(block.start)
            const free = parseCalendarDate(block.end_exclusive)
            const nights = first && free ? differenceInCalendarDays(free, first) : null
            const status = blockStatusWords(block, blocksData)
            const overlap = overlapPhrase(block, blockPool)
            const detail = [
              nights ? `${nights} ${nights === 1 ? 'night' : 'nights'}` : null,
              `${shortDay(block.start)} to ${shortDay(block.end_exclusive)}`,
              block.note,
              status,
              overlap,
            ]
              .filter(Boolean)
              .join(' · ')
            return {
              key: `block:${block.kind}:${block.id}`,
              tileClassName: block.kind === 'uplisting' ? HOLD_BAR_CLASSES : BLOCK_BAR_CLASSES,
              icon: <Lock className="h-4 w-4" aria-hidden="true" />,
              title: blockLabel(block.kind),
              detail,
              ariaLabel: `${blockLabel(block.kind)}, ${detail} - open ${
                block.kind === 'owner' ? 'block' : 'hold'
              }`,
              testId: 'day-list-block-row',
              data: { 'data-block-kind': block.kind },
              onOpen: () => openBlockSheet(go, block, agenda ? undefined : day),
            }
          })
      : []

  return nonEmptyGroups([
    { id: 'stays', heading: 'Stays', rows: stayRows },
    { id: 'cleanings', heading: 'Cleanings', rows: cleaningRows },
    { id: 'deposits', heading: 'Deposits', rows: depositRows },
    { id: 'supplies', heading: 'Supplies', rows: supplyRows },
    { id: 'reviews', heading: 'Reviews', rows: reviewRows },
    { id: 'gaps', heading: 'Empty nights', rows: gapRows },
    { id: 'blocks', heading: 'Blocks', rows: blockRows },
  ])
}
